import { compactVerify, decodeJwt, decodeProtectedHeader, importSPKI, jwtVerify } from 'jose';
import type { ActivationClaims, ActivationState } from './ActivationClaims';
import { ACTIVATION_AUDIENCE, ACTIVATION_ISSUER } from './activationConstants';
import { LicensePolicyService } from './LicensePolicyService';
import { resolvePublicKey } from './PublicKeyRegistry';

export type PublicKeyResolver = (kid: string | undefined) => string | null;

export class ActivationCertificateVerifier {
  constructor(
    private readonly policy = new LicensePolicyService(),
    private readonly keyResolver: PublicKeyResolver = resolvePublicKey,
  ) {}

  async verifyCertificate(
    jwt: string,
    deviceFingerprint: string,
    currentTenantId: string,
    currentSalariesCount: number,
    nowSec: number = Math.floor(Date.now() / 1000),
  ): Promise<ActivationState> {
    try {
      const { kid } = decodeProtectedHeader(jwt);
      const pem = this.keyResolver(kid);
      if (!pem) {
        return { valid: false, errorCode: 'INVALID_SIGNATURE', isReadOnly: true };
      }

      const pubKey = await importSPKI(pem, 'ES256');
      const { payload } = await jwtVerify(jwt, pubKey, {
        algorithms: ['ES256'],
        issuer: ACTIVATION_ISSUER,
        audience: ACTIVATION_AUDIENCE,
        currentDate: new Date(nowSec * 1000),
        clockTolerance: 300,
      });

      const claims = payload as unknown as ActivationClaims;
      return this.policy.evaluateLocalPolicy(
        claims,
        deviceFingerprint,
        currentTenantId,
        currentSalariesCount,
        nowSec,
      );
    } catch (err) {
      const jwtError = err as { code?: string; claim?: string };
      if (jwtError.code === 'ERR_JWT_EXPIRED' || jwtError.claim === 'exp') {
        try {
          const { kid } = decodeProtectedHeader(jwt);
          const pem = this.keyResolver(kid);
          if (!pem) {
            return { valid: false, errorCode: 'INVALID_SIGNATURE', isReadOnly: true };
          }
          const pubKey = await importSPKI(pem, 'ES256');
          await compactVerify(jwt, pubKey);
          const claims = decodeJwt(jwt) as unknown as ActivationClaims;
          return this.policy.evaluateLocalPolicy(
            claims,
            deviceFingerprint,
            currentTenantId,
            currentSalariesCount,
            nowSec,
          );
        } catch {
          return { valid: false, errorCode: 'INVALID_SIGNATURE', isReadOnly: true };
        }
      }
      const errorCode: ActivationState['errorCode'] =
        jwtError.claim === 'nbf' ? 'NOT_YET_VALID' : 'INVALID_SIGNATURE';
      return { valid: false, errorCode, isReadOnly: true };
    }
  }
}
